import { useMemo, useRef } from 'react';
import axios, { AxiosInstance } from 'axios';
import axiosRetry from 'axios-retry';

interface UseUnauthenticatedApiOptions {
    baseUrl: string;
    axiosInstance?: AxiosInstance;
}

interface UseUnauthenticatedApiResponse {
    api: AxiosInstance;
}

function useUnauthenticatedApi({
    baseUrl,
    axiosInstance
}: UseUnauthenticatedApiOptions): UseUnauthenticatedApiResponse {
    const api = useMemo(() => {
        const api = axiosInstance ? axiosInstance : axios.create();
        api.defaults.timeout = 30000; // 30 seconds
        axiosRetry(api, {
            retries: 3,
            shouldResetTimeout: true,
            // eslint-disable-next-line @typescript-eslint/unbound-method
            retryDelay: axiosRetry.exponentialDelay,
            retryCondition: (error) => {
                // if retry condition is not specified, by default idempotent requests are retried
                if (!error.response) {
                    return true;
                }
                return error.response.status >= 500;
            }
        });

        return api;
    }, [axiosInstance]);

    const requestInterceptorRef = useRef<number | null>(null);

    if (requestInterceptorRef.current !== null) {
        api.interceptors.request.eject(requestInterceptorRef.current);
    }

    requestInterceptorRef.current = api.interceptors.request.use((config) => {
        config.baseURL = baseUrl;

        return config;
    });

    return {
        api
    };
}

export { useUnauthenticatedApi };
